import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowLeft, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { Logo } from "@/components/Logo";
import { DemoRequestModal } from "@/components/DemoRequestModal";

const QUOTA_ROWS = [
  ["users_included", "Utilisateurs inclus"],
  ["data_sources", "Sources de données"],
  ["ai_usage", "Usage IA"],
  ["destinations_properties", "Destinations / établissements"],
  ["partner_access", "Accès partenaires"],
  ["campaign_features", "Campagnes"],
  ["reporting_level", "Reporting"],
  ["support_level", "Support"],
];

const priceLabel = (plan, annual) => {
  if (plan.custom_pricing) return { big: "Sur mesure", small: "" };
  const val = annual ? plan.annual_price : plan.monthly_price;
  if (val == null) return { big: "Sur mesure", small: "" };
  return { big: `${val.toLocaleString("fr-FR")} ${plan.currency}`, small: annual ? "/ an" : "/ mois" };
};

const isPopular = (plan) => (plan.highlights || []).some((h) => /populaire/i.test(h));

// Savings when committing annually vs paying 12× the monthly price.
const annualSavings = (plan) => {
  if (plan.custom_pricing || plan.monthly_price == null || plan.annual_price == null) return null;
  const full = plan.monthly_price * 12;
  const saved = full - plan.annual_price;
  if (saved <= 0) return null;
  return { amount: saved, months: Math.round(saved / plan.monthly_price), full };
};

function PlanCard({ plan, annual, onChoose, contactLabel }) {
  const price = priceLabel(plan, annual);
  const popular = isPopular(plan);
  const contact = plan.cta_type === "contact";
  const savings = annualSavings(plan);
  return (
    <div data-testid={`pricing-card-${plan.key}`}
      className={`relative flex flex-col rounded-2xl border bg-white p-6 ${popular ? "border-[#3E7256] shadow-lg ring-1 ring-[#3E7256]" : "border-slate-200"}`}>
      {popular && <span className="absolute -top-3 left-6 rounded-full bg-[#1E3D2B] px-3 py-1 text-xs font-semibold text-white">Le plus populaire</span>}
      <h3 className="font-heading text-xl font-bold text-[#17201A]">{plan.name}</h3>
      <div className="mt-3 flex items-end gap-1">
        <span className="font-heading text-3xl font-bold text-[#17201A]">{price.big}</span>
        {price.small && <span className="pb-1 text-sm text-slate-400">{price.small}</span>}
      </div>
      {annual && savings ? (
        <div className="mt-2 flex items-center gap-2" data-testid={`pricing-savings-${plan.key}`}>
          <span className="inline-flex items-center rounded-full bg-[#E8F0EA] px-2 py-0.5 text-xs font-bold text-[#1E3D2B]">−{savings.months} mois</span>
          <span className="text-xs text-slate-400 line-through">{savings.full.toLocaleString("fr-FR")} {plan.currency}</span>
          <span className="text-xs font-semibold text-[#3E7256]">économie {savings.amount.toLocaleString("fr-FR")} {plan.currency}/an</span>
        </div>
      ) : null}
      {(plan.highlights || []).filter((h) => !/populaire/i.test(h)).map((h, i) => (
        <p key={i} className="mt-2 text-sm text-[#3E7256]">{h}</p>
      ))}
      <ul className="mt-5 flex-1 space-y-2.5">
        {QUOTA_ROWS.map(([k, label]) => (
          <li key={k} className="flex items-start gap-2 text-sm text-[#374151]">
            <Check size={15} className="mt-0.5 shrink-0 text-[#5C9873]" />
            <span className="flex min-w-0 flex-col gap-x-1 sm:flex-row sm:flex-wrap">
              <span className="text-slate-400">{label} :</span>
              <span className="break-words">{plan.quotas?.[k] || "—"}</span>
            </span>
          </li>
        ))}
        <li className="flex items-start gap-2 text-sm text-[#374151]">
          <Check size={15} className={`mt-0.5 shrink-0 ${plan.quotas?.gsa_available ? "text-[#5C9873]" : "text-slate-300"}`} />
          <span className="flex min-w-0 flex-col gap-x-1 sm:flex-row sm:flex-wrap">
            <span className="text-slate-400">GSA Worldwide :</span>
            <span className="break-words">{plan.quotas?.gsa_available ? "Disponible en option" : "Non disponible"}</span>
          </span>
        </li>
      </ul>
      <button data-testid={`pricing-cta-${plan.key}`} onClick={() => onChoose(plan)}
        className={`mt-6 w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5C9873] focus-visible:ring-offset-2 ${contact ? "border border-[#1E3D2B] text-[#1E3D2B] hover:bg-[#1E3D2B] hover:text-white" : "bg-[#1E3D2B] text-white hover:bg-[#2C5540]"}`}>
        {contact ? contactLabel : "Choose plan"}
      </button>
    </div>
  );
}

function ComparisonTable({ plans, annual, onChoose, contactLabel }) {
  if (!plans.length) return null;
  const rows = [
    ["Prix", (p) => {
      if (p.custom_pricing) return "Sur mesure";
      const v = annual ? p.annual_price : p.monthly_price;
      return v == null ? "Sur mesure" : `${v.toLocaleString("fr-FR")} ${p.currency} ${annual ? "/ an" : "/ mois"}`;
    }],
    ...QUOTA_ROWS.map(([k, label]) => [label, (p) => p.quotas?.[k] || "—"]),
    ["GSA Worldwide", (p) => (p.quotas?.gsa_available ? true : false)],
  ];
  return (
    <div className="mt-14" data-testid="pricing-comparison">
      <h2 className="mb-1 text-center font-heading text-2xl font-bold text-[#17201A]">Comparer les offres en détail</h2>
      <p className="mb-6 text-center text-sm text-slate-500">Toutes les fonctionnalités, plan par plan.</p>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">Fonctionnalité</th>
              {plans.map((p) => (
                <th key={p.id} className={`px-5 py-4 text-left ${isPopular(p) ? "bg-[#F2F7F3]" : ""}`}>
                  <span className="font-heading text-base font-bold text-[#17201A]">{p.name}</span>
                  {isPopular(p) && <span className="ml-2 rounded-full bg-[#1E3D2B] px-2 py-0.5 text-[10px] font-semibold text-white">Populaire</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, fn], ri) => (
              <tr key={ri} className="border-b border-slate-100 last:border-0">
                <td className="px-5 py-3 text-sm font-medium text-slate-500">{label}</td>
                {plans.map((p) => {
                  const val = fn(p);
                  return (
                    <td key={p.id} className={`px-5 py-3 text-sm text-[#17201A] ${isPopular(p) ? "bg-[#F7FAF8]" : ""}`}>
                      {typeof val === "boolean"
                        ? (val ? <Check size={16} className="text-[#3E7256]" /> : <X size={16} className="text-slate-300" />)
                        : <span className={ri === 0 ? "font-semibold" : ""}>{val}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td className="px-5 py-4" />
              {plans.map((p) => (
                <td key={p.id} className={`px-5 py-4 ${isPopular(p) ? "bg-[#F7FAF8]" : ""}`}>
                  <button data-testid={`compare-cta-${p.key}`} onClick={() => onChoose(p)}
                    className={`w-full rounded-lg px-3 py-2 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5C9873] focus-visible:ring-offset-2 ${p.cta_type === "contact" ? "border border-[#1E3D2B] text-[#1E3D2B] hover:bg-[#1E3D2B] hover:text-white" : "bg-[#1E3D2B] text-white hover:bg-[#2C5540]"}`}>
                    {p.cta_type === "contact" ? contactLabel : "Choose plan"}
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function PricingPage() {
  const { user, activeMembership } = useAuth();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactContext, setContactContext] = useState("");

  useEffect(() => { api.get("/pricing/plans").then((r) => setData(r.data)).catch(() => setData({ plans: [], addons: [] })); }, []);

  const contactLabel = lang === "en" ? "Contact us" : "Nous contacter";

  const onChoose = (plan) => {
    if (plan.cta_type === "contact" || plan.custom_pricing) {
      const offer = plan.name || "Sur mesure";
      setContactContext(
        lang === "en"
          ? `Interested in the Enterprise / custom offer (${offer}).`
          : `Intérêt pour l'offre Enterprise / Sur mesure (${offer}).`
      );
      setContactOpen(true);
      return;
    }
    toast.success(`Plan « ${plan.name} » sélectionné — le paiement sera bientôt disponible (démo).`);
  };

  const maxMonths = (data?.plans || []).reduce((m, p) => {
    const s = annualSavings(p);
    return s ? Math.max(m, s.months) : m;
  }, 0);

  return (
    <div className="min-h-screen bg-[#FAFAF6]" data-testid="pricing-page">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
        <Link to={user ? "/dashboard" : "/login"} className="flex items-center gap-3">
          <Logo className="h-8 w-8" />
          <span className="font-heading text-sm font-bold text-[#17201A]">Lodge &amp; Co</span>
        </Link>
        <Link to={user ? "/dashboard" : "/login"} data-testid="pricing-back"
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          <ArrowLeft size={15} /> {user ? "Retour au tableau de bord" : "Connexion"}
        </Link>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-[#17201A] sm:text-5xl">Des offres adaptées à chaque acteur du tourisme</h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-500">Choisissez le plan qui correspond à votre ambition. L'option GSA Worldwide s'ajoute à n'importe quel plan et tout type d'organisation.</p>
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white p-1" data-testid="pricing-billing-toggle">
            <button onClick={() => setAnnual(false)} data-testid="billing-monthly" className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${!annual ? "bg-[#1E3D2B] text-white" : "text-slate-500"}`}>Mensuel</button>
            <button onClick={() => setAnnual(true)} data-testid="billing-annual" className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${annual ? "bg-[#1E3D2B] text-white" : "text-slate-500"}`}>
              Annuel
              {maxMonths > 0 && (
                <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${annual ? "bg-white/20 text-white" : "bg-[#E8F0EA] text-[#1E3D2B]"}`}>−{maxMonths} mois</span>
              )}
            </button>
          </div>
          {maxMonths > 0 && (
            <p className="mt-3 text-sm font-semibold text-[#3E7256]" data-testid="pricing-annual-hint">
              Passez à l'année et économisez jusqu'à {maxMonths} mois offerts.
            </p>
          )}
        </div>

        {!data ? (
          <div className="flex items-center justify-center gap-2 py-24 text-slate-400"><Loader2 className="animate-spin" size={18} /> Chargement des offres…</div>
        ) : (
          <>
            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              {data.plans.map((p) => <PlanCard key={p.id} plan={p} annual={annual} onChoose={onChoose} contactLabel={contactLabel} />)}
            </div>

            {data.addons.map((addon) => (
              <div key={addon.id} data-testid={`pricing-addon-${addon.key}`}
                className="mt-8 flex flex-col items-start justify-between gap-4 rounded-2xl border border-[#1E3D2B] bg-[#1E3D2B] p-8 text-white md:flex-row md:items-center">
                <div className="flex items-start gap-4">
                  <div className="rounded-xl bg-white/10 p-3"><Globe size={24} /></div>
                  <div>
                    <h3 className="font-heading text-xl font-bold">{addon.name}</h3>
                    <p className="mt-1 max-w-2xl text-sm text-white/70">Option de représentation commerciale internationale, activable sur n'importe quel plan et pour tout type d'organisation (destination, hôtel, lodge, collection, DMC, compagnie aérienne, autre acteur touristique).</p>
                    <p className="mt-2 text-sm font-semibold text-[#9DC4AE]">{addon.custom_pricing ? "Custom pricing" : `${(addon.monthly_price ?? 0).toLocaleString("fr-FR")} ${addon.currency} / mois`}</p>
                  </div>
                </div>
                <button data-testid={`pricing-cta-${addon.key}`} onClick={() => onChoose(addon)}
                  className="shrink-0 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-[#1E3D2B] hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1E3D2B]">
                  {contactLabel}
                </button>
              </div>
            ))}

            <ComparisonTable plans={data.plans} annual={annual} onChoose={onChoose} contactLabel={contactLabel} />

            <p className="mt-8 text-center text-xs text-slate-400">Les tarifs affichés sont temporaires et fournis à titre de démonstration. Aucun paiement n'est traité à ce stade.</p>

            <DemoRequestModal
              renderTrigger={false}
              controlledOpen={contactOpen}
              onControlledOpenChange={setContactOpen}
              prefill={{ organization: activeMembership?.org?.name, email: user?.email }}
              contextLabel={contactContext}
              source="pricing_enterprise"
            />
          </>
        )}
      </main>
    </div>
  );
}
